import { last, first, omit } from 'lodash';
import { useMemo, useCallback } from 'react';
import { useInfiniteQuery, QueryKey, QueryStatus } from 'react-query';

import { createRestApi, DefaultGetAllResponse } from '~/App/Api/rest';

export interface PaginationOptions {
  perPage?: number;
  perPageKey?: string;
  requestParams?: Record<string, string | number | null | undefined | string[]>;
  infinite?: boolean;
  itemUrl?: string | null;
  key: QueryKey;
}

export interface Paginator<E> {
  load(page: number): Promise<DefaultGetAllResponse<E>[] | undefined>;
  more(): Promise<void>;
}

export interface PaginationMetadata {
  total?: number;
  currentPage?: number;
  loading: boolean;
  fetching: boolean;
  isFetching: boolean;
  canFetchMore?: boolean;
  cached: boolean;
  perPage: number;
  status: QueryStatus;
}

export type UsePagination<E, P extends PaginationMetadata> = [
  E[],
  Paginator<E>,
  P
];

const defaultOptions: Omit<PaginationOptions, 'key'> = {
  perPage: 15,
  perPageKey: 'per_page',
  requestParams: {},
  infinite: true,
  itemUrl: null,
};

function usePagination<
  E = unknown,
  P extends PaginationMetadata = PaginationMetadata
>(url: string, runtimeOptions: PaginationOptions): UsePagination<E, P> {
  const options: PaginationOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...(runtimeOptions || {}),
    }),
    [runtimeOptions]
  );

  const requestParams = useMemo(
    () => ({
      ...(options.requestParams || {}),
      [options.perPageKey!]: options.perPage,
    }),
    [options]
  );

  const api = useMemo(() => createRestApi<E>(url), [url]);

  const loadPage = useCallback(
    async ({ pageParam = 1 }: { pageParam?: number }) => {
      const res = await api.getAll({
        ...requestParams,
        page: pageParam,
      });

      return res;
    },
    [requestParams, api]
  );

  const queryKey: QueryKey = useMemo(() => {
    const baseKey = [...(options.key ?? url)];

    if (options.infinite) {
      return [...baseKey, 'infinite', requestParams];
    }

    return [...baseKey, requestParams];
  }, [options, url, requestParams]);

  const getFetchMore = (lastResponse: DefaultGetAllResponse<E>) => {
    const nextPage = lastResponse.current_page + 1;

    // if `last_page` key is present
    if (
      lastResponse.last_page &&
      lastResponse.last_page > lastResponse.current_page
    ) {
      return nextPage;
    }

    // there are more records if next_page_url is provided
    if (lastResponse.next_page_url != null) {
      return nextPage;
    }

    return null;
  };

  const pagination = useInfiniteQuery<
    DefaultGetAllResponse<E>,
    typeof queryKey
  >(queryKey, loadPage, {
    useErrorBoundary: false,
    getNextPageParam: getFetchMore,
  });

  const lastPage = useMemo<Partial<DefaultGetAllResponse<E>>>(() => {
    return last(pagination.data?.pages) || {};
  }, [pagination]);

  const allMetadata = useMemo<P>(() => {
    // blacklist irrelevant attributes from laravel pagination for the metadata
    const paginationBlacklist = [
      'data',
      'from',
      'to',
      'per_page',
      'current_page',
      'currentPage',
      'next_page_url',
      'prev_page_url',
    ];

    const resultsMetadata = omit(
      first(pagination.data?.pages),
      paginationBlacklist
    );

    return {
      ...resultsMetadata,
      total: lastPage.total,
      currentPage: lastPage.current_page,
      loading: pagination.isLoading,
      // fetching is deprecated in favor of isLoading in useQuery. retaining it here to handle codes still calling fetching in usePagination
      fetching: pagination.isLoading,
      isFetching: pagination.isFetching,
      canFetchMore: pagination.hasNextPage,
      cached: !pagination.isLoading,
      perPage: options.perPage,
      status: pagination.status,
    } as P;
  }, [lastPage, pagination, options]);

  const fetchPageOrNext = useCallback(
    async (pageParam?: number) => {
      if (pagination.isFetchingNextPage) {
        return;
      }

      if (typeof pageParam === 'number') {
        return (await pagination.fetchNextPage({
          pageParam,
        })) as unknown as DefaultGetAllResponse<E>[];
      }

      if (pagination.hasNextPage) {
        return (await pagination.fetchNextPage()) as unknown as DefaultGetAllResponse<E>[];
      }
    },
    [pagination]
  );

  const { data, paginator, metadata } = useMemo(
    () => ({
      data: (pagination.data?.pages ?? []).reduce(
        (data: E[], page: DefaultGetAllResponse<E>) => [...data, ...page.data],
        []
      ),
      paginator: {
        load: fetchPageOrNext,
        more: async () => {
          await fetchPageOrNext();
        },
      },
      metadata: allMetadata,
    }),
    [pagination, allMetadata]
  );

  return [data, paginator, metadata];
}

export default usePagination;
